import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Button, Flex, Input, message, Radio } from "antd";
import { MaterialIcon } from "@/shared/ui/MaterialIcon";
import type {
  AIAwaitFormSubmitParamData,
  AIAwaitSubmitPayloadData,
  FormActiveAwaiting,
} from "@/app/state/types";
import { getViewport } from "@/shared/data";
import {
  type AwaitingCollectDecision,
  buildAwaitingCollectMessage,
  buildAwaitingInitMessage,
  buildAwaitingUpdateMessage,
  buildAwaitingViewportSignature,
  isAwaitingFrameCloseMessage,
  readAwaitingSubmitPayload,
} from "@/features/tools/components/protocol";
import { useAwaitingTimeoutCountdown } from "@/features/tools/components/awaitingTimeout";
import { useI18n } from "@/shared/i18n";
import { useKeyboard } from "@/shared/utils/useKeyboard";
import { RadioRef } from "antd/es/radio";
import "./hitlCompat.module.css";

interface AwaitingHtmlContainerProps {
  data: FormActiveAwaiting;
  onPatch?: (patch: Partial<FormActiveAwaiting>) => void;
  onSubmit?: (payload: AIAwaitSubmitPayloadData) => Promise<unknown>;
  onClose?: () => void;
  onResolved?: () => void;
}

const AWAITING_PANEL_CLASS_NAME =
  "awaiting-panel tw:flex tw:min-h-[var(--composer-main-min-height)] tw:w-full tw:flex-1 tw:flex-col tw:gap-2.5 tw:rounded-2xl tw:border tw:border-line-soft tw:bg-bg-card tw:px-3 tw:py-2.5 tw:shadow-elevated";

const AWAITING_PANEL_HEADER_CLASS_NAME =
  "awaiting-panel-header tw:flex tw:flex-wrap tw:items-baseline tw:justify-between tw:gap-x-3 tw:gap-y-2";

const AWAITING_PANEL_HEADER_MAIN_CLASS_NAME =
  "awaiting-panel-header-main tw:flex tw:flex-wrap tw:items-baseline tw:gap-x-3 tw:gap-y-2";

const AWAITING_PANEL_HEADER_SIDE_CLASS_NAME =
  "awaiting-panel-header-side tw:ml-auto tw:flex tw:flex-wrap tw:items-center tw:gap-2";

const AWAITING_PANEL_TITLE_CLASS_NAME =
  "awaiting-panel-title tw:text-sm tw:font-extrabold tw:text-ink-1";

const AWAITING_TIMEOUT_BADGE_CLASS_NAME =
  "awaiting-timeout-badge tw:whitespace-nowrap tw:text-xs tw:font-bold tw:text-text-muted";

const AWAITING_PANEL_FORM_SWITCHER_CLASS_NAME =
  "awaiting-panel-form-switcher tw:flex tw:items-center tw:gap-1";

const AWAITING_PANEL_FORM_SWITCHER_LABEL_CLASS_NAME =
  "awaiting-panel-form-switcher-label tw:max-w-[min(320px,55vw)] tw:overflow-hidden tw:text-ellipsis tw:whitespace-nowrap tw:text-[11px] tw:font-bold tw:text-ink-muted";

const FRONTEND_TOOL_FRAME_CLASS_NAME =
  "frontend-tool-frame tw:w-full tw:max-h-[70vh] tw:border-0 tw:[.layout-copilot_&]:h-[min(320px,44vh)]";

const AWAITING_PANEL_ERROR_CLASS_NAME =
  "awaiting-panel-error tw:rounded-xl tw:border tw:border-[rgba(246,222,227,0.95)] tw:bg-[linear-gradient(180deg,rgba(255,248,249,0.96),rgba(255,239,243,0.96))] tw:px-2.5 tw:py-2 tw:text-[11px] tw:font-semibold tw:text-accent-danger";

const AWAITING_PANEL_EMPTY_CLASS_NAME =
  "awaiting-panel-empty tw:rounded-xl tw:bg-[rgba(248,250,254,0.92)] tw:px-2.5 tw:py-2 tw:text-[11px] tw:font-semibold tw:text-ink-muted";

const AWAITING_PANEL_FOOTER_CLASS_NAME =
  "awaiting-panel-footer tw:mt-0.5 tw:flex tw:flex-col tw:items-stretch tw:gap-2.5";

const AWAITING_PANEL_RADIOGROUP_CLASS_NAME =
  "awaiting-panel-radiogroup tw:flex tw:flex-col tw:gap-0.5 tw:text-xs";

const AWAITING_PANEL_OPTION_CLASS_NAME =
  "awaiting-panel-option tw:mr-0 tw:min-w-0 tw:flex-1 tw:rounded-2xl tw:border tw:border-transparent tw:py-1.5 tw:text-xs tw:text-text-muted tw:hover:bg-bg-hover tw:focus-within:bg-bg-hover";

const AWAITING_PANEL_FREE_TEXT_OPTION_CLASS_NAME =
  "awaiting-panel-option free-text tw:mr-0 tw:min-w-0 tw:flex-1 tw:rounded-2xl tw:border tw:border-transparent tw:py-1.5 tw:text-xs tw:text-text-muted tw:hover:bg-bg-hover tw:focus-within:bg-bg-hover tw:focus-within:border-accent";

const AWAITING_PANEL_OPTION_INDEX_CLASS_NAME =
  "awaiting-panel-option-index tw:flex tw:h-5 tw:w-5 tw:shrink-0 tw:items-center tw:justify-center tw:rounded-full tw:border tw:border-line tw:bg-bg-hover";

const AWAITING_PANEL_OPTION_LABEL_CLASS_NAME =
  "awaiting-panel-option-label tw:whitespace-nowrap tw:font-semibold tw:text-text-main";

const AWAITING_PANEL_SUBMIT_TAIL_CLASS_NAME =
  "awaiting-panel-submit-tail tw:text-text-muted";

export const INVALID_AWAITING_SUBMIT_ERROR = "Invalid awaiting submit payload";

function getSubmitErrorText(result: unknown): string {
  if (typeof result === "string" && result.trim()) {
    return result.trim();
  }
  if (result instanceof Error && result.message.trim()) {
    return result.message.trim();
  }
  return "";
}

export const AWAITING_COLLECT_TIMEOUT_MS = 5_000;
export const AWAITING_COLLECT_TIMEOUT_ERROR =
  "Awaiting form did not respond to the collect request";

type AwaitingCollectFlow =
  | { type: "submit" }
  | { type: "reject"; reason: string }
  | { type: "switch"; nextIndex: number };

type AwaitingFooterDecision = "submit" | "reject";

interface AwaitingCollectLifecycleHandlers {
  onCollectingChange: (decision: AwaitingCollectDecision | null) => void;
  onStatusChange: (status: string) => void;
  onErrorChange: (error: string) => void;
}

export interface AwaitingInitFrame {
  addEventListener: (type: "load", listener: () => void) => void;
  removeEventListener: (type: "load", listener: () => void) => void;
}

export function bindAwaitingInitListener(
  frame: AwaitingInitFrame,
  sendInit: () => void,
): () => void {
  frame.addEventListener("load", sendInit);
  sendInit();
  return () => {
    frame.removeEventListener("load", sendInit);
  };
}

export function beginAwaitingCollectRequest(
  input: {
    awaiting: FormActiveAwaiting;
    decision: AwaitingCollectDecision;
    postMessage: (message: unknown, targetOrigin: string) => void;
    scheduleTimeout: (
      callback: () => void,
      delay: number,
    ) => ReturnType<typeof setTimeout>;
  } & AwaitingCollectLifecycleHandlers,
): ReturnType<typeof setTimeout> {
  const {
    awaiting,
    decision,
    postMessage,
    scheduleTimeout,
    onCollectingChange,
    onStatusChange,
    onErrorChange,
  } = input;

  postMessage(buildAwaitingCollectMessage(awaiting, decision), "*");
  onCollectingChange(decision);
  onStatusChange("collecting");
  onErrorChange("");

  return scheduleTimeout(() => {
    onCollectingChange(null);
    onStatusChange("");
    onErrorChange(AWAITING_COLLECT_TIMEOUT_ERROR);
  }, AWAITING_COLLECT_TIMEOUT_MS);
}

export function clearAwaitingCollectRequest(
  clearTimeoutFn: (timeout: ReturnType<typeof setTimeout>) => void,
  timeout: ReturnType<typeof setTimeout> | null,
  handlers?: Partial<AwaitingCollectLifecycleHandlers>,
): void {
  if (timeout) {
    clearTimeoutFn(timeout);
  }
  handlers?.onCollectingChange?.(null);
  handlers?.onStatusChange?.("");
}

export function reportInvalidAwaitingSubmitPayload(
  awaitingId: string,
  eventData: unknown,
  onErrorChange: (error: string) => void,
): void {
  console.warn("[awaiting-html] invalid frontend_awaiting_submit payload", {
    awaitingId,
    eventData,
  });
  onErrorChange(INVALID_AWAITING_SUBMIT_ERROR);
}

function clampAwaitingFormIndex(index: number, formsLength: number): number {
  if (formsLength <= 1) {
    return 0;
  }
  return Math.min(formsLength - 1, Math.max(0, index));
}

function cloneAwaitingFormData(
  form: Record<string, any> | null | undefined,
): Record<string, any> | null {
  return form ? { ...form } : null;
}

function hasFormField(param: AIAwaitFormSubmitParamData): boolean {
  return Object.prototype.hasOwnProperty.call(param, "form");
}

function buildRejectParam(
  id: string,
  reason?: string,
  form?: Record<string, any> | null,
): AIAwaitFormSubmitParamData {
  const trimmedReason = typeof reason === "string" ? reason.trim() : "";
  return {
    id,
    decision: "reject",
    ...(trimmedReason ? { reason: trimmedReason } : {}),
    ...(form !== undefined ? { form: cloneAwaitingFormData(form) } : {}),
  };
}

function resizeAwaitingIframe(iframe: HTMLIFrameElement): void {
  try {
    const doc = iframe.contentDocument || iframe.contentWindow?.document;
    if (doc?.body) {
      const height = Math.max(doc.body.scrollHeight, 100);
      iframe.style.height = `${height}px`;

      setTimeout(() => {
        try {
          const nextHeight = Math.max(doc.body.scrollHeight, 100);
          iframe.style.height = `${nextHeight}px`;
        } catch {
          /* ignore */
        }
      }, 500);
      return;
    }
  } catch {
    /* ignore */
  }

  iframe.style.height = "300px";
}

export function mergeSubmittedParamsIntoAwaitingForms(
  forms: FormActiveAwaiting["forms"],
  params: AIAwaitFormSubmitParamData[],
): FormActiveAwaiting["forms"] {
  const formById = new Map<string, Record<string, unknown> | null>();

  for (const param of params) {
    if (
      (param.decision !== "approve" && param.decision !== "reject") ||
      !hasFormField(param)
    ) {
      continue;
    }
    formById.set(param.id, cloneAwaitingFormData(param.form));
  }

  if (formById.size === 0) {
    return forms;
  }

  return forms.map((form) => {
    if (!formById.has(form.id)) {
      return form;
    }
    return {
      ...form,
      form: formById.get(form.id) ?? null,
    };
  });
}

export function buildAggregatedAwaitingSubmitPayload(
  awaiting: FormActiveAwaiting,
  collectedParams: AIAwaitFormSubmitParamData[],
): AIAwaitSubmitPayloadData {
  const collectedParamById = new Map<string, AIAwaitFormSubmitParamData>();
  const firstCollectedDecision = collectedParams[0]?.decision;
  const sharedNonSubmitDecision =
    firstCollectedDecision === "reject" &&
    collectedParams.length > 0 &&
    collectedParams.every((param) => param.decision === firstCollectedDecision)
      ? firstCollectedDecision
      : null;

  if (sharedNonSubmitDecision === "reject") {
    const sharedCollectedParamById = new Map(
      collectedParams.map((param) => [param.id, param]),
    );
    return {
      runId: awaiting.runId,
      awaitingId: awaiting.awaitingId,
      params: awaiting.forms.map((form) => {
        const collected = sharedCollectedParamById.get(form.id);
        return buildRejectParam(
          form.id,
          collectedParams[0]?.reason,
          collected && hasFormField(collected) ? collected.form : form.form,
        );
      }),
    };
  }

  for (const param of collectedParams) {
    collectedParamById.set(
      param.id,
      param.decision === "approve"
        ? {
            ...param,
            ...(hasFormField(param)
              ? { form: cloneAwaitingFormData(param.form) }
              : {}),
          }
        : { ...param },
    );
  }

  const params: AIAwaitFormSubmitParamData[] = awaiting.forms.map((form) => {
    const collected = collectedParamById.get(form.id);
    collectedParamById.delete(form.id);
    if (collected?.decision === "reject") {
      return buildRejectParam(
        form.id,
        collected.reason,
        hasFormField(collected) ? collected.form : form.form,
      );
    }
    const submittedForm =
      collected?.decision === "approve" && hasFormField(collected)
        ? cloneAwaitingFormData(collected.form)
        : cloneAwaitingFormData(form.form);
    return {
      id: form.id,
      decision: "approve" as const,
      form: submittedForm,
    };
  });

  for (const param of collectedParamById.values()) {
    if (param.decision === "approve") {
      params.push({
        id: param.id,
        decision: "approve",
        ...(hasFormField(param)
          ? ({ form: cloneAwaitingFormData(param.form) } as any)
          : {}),
      });
      continue;
    }
    if (param.decision === "reject") {
      params.push(
        buildRejectParam(
          param.id,
          param.reason,
          hasFormField(param) ? param.form : undefined,
        ),
      );
    }
  }

  return {
    runId: awaiting.runId,
    awaitingId: awaiting.awaitingId,
    params,
  };
}

export function buildCancelAwaitingSubmitPayload(
  awaiting: FormActiveAwaiting,
): AIAwaitSubmitPayloadData {
  return {
    runId: awaiting.runId,
    awaitingId: awaiting.awaitingId,
    params: awaiting.forms.map((form) => ({
      id: form.id,
      decision: "reject" as const,
    })),
  };
}

export function buildRejectAwaitingSubmitPayload(
  awaiting: FormActiveAwaiting,
  reason?: string,
  forms?: FormActiveAwaiting["forms"],
): AIAwaitSubmitPayloadData {
  const sourceForms = forms ?? awaiting.forms;
  return {
    runId: awaiting.runId,
    awaitingId: awaiting.awaitingId,
    params: sourceForms.map((form) =>
      buildRejectParam(form.id, reason, form.form),
    ),
  };
}

export const AwaitingHtmlContainer: React.FC<AwaitingHtmlContainerProps> = ({
  data,
  onPatch,
  onSubmit,
  onClose,
  onResolved,
}) => {
  const { t } = useI18n();
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const activeKeyRef = useRef(data.key);
  const requestedKeyRef = useRef("");
  const currentFrameKeyRef = useRef("");
  const lastPostedSignatureRef = useRef("");
  const resolutionHandledRef = useRef(false);
  const collectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const collectFlowRef = useRef<AwaitingCollectFlow | null>(null);
  const [activeFormIndex, setActiveFormIndex] = useState(0);
  const [submitStatus, setSubmitStatus] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [timeoutExpired, setTimeoutExpired] = useState(false);
  const [collectingDecision, setCollectingDecision] =
    useState<AwaitingCollectDecision | null>(null);
  const [rejectReason, setRejectReason] = useState("");
  const [footerDecision, setFooterDecision] =
    useState<AwaitingFooterDecision>();
  const currentForm = data.forms[activeFormIndex];
  const resolved = Boolean(data.resolutionReason);
  const panelCaption = String(
    currentForm?.title ||
      currentForm?.action ||
      currentForm?.id ||
      data.viewportKey ||
      "",
  ).trim();

  const viewportSignature = useMemo(
    () => buildAwaitingViewportSignature(data, activeFormIndex),
    [activeFormIndex, data],
  );

  const clearCollectTimeout = useCallback(() => {
    if (!collectTimeoutRef.current) {
      return;
    }
    clearTimeout(collectTimeoutRef.current);
    collectTimeoutRef.current = null;
  }, []);

  const postToFrame = useCallback(
    (kind: "init" | "update") => {
      const frame = iframeRef.current;
      if (!frame?.contentWindow) {
        return;
      }

      frame.contentWindow.postMessage(
        kind === "init"
          ? buildAwaitingInitMessage(data, activeFormIndex)
          : buildAwaitingUpdateMessage(data, activeFormIndex),
        "*",
      );
      lastPostedSignatureRef.current = viewportSignature;
      resizeAwaitingIframe(frame);
    },
    [activeFormIndex, data, viewportSignature],
  );

  const requestCollectFromFrame = useCallback(
    (decision: AwaitingCollectDecision, flow: AwaitingCollectFlow) => {
      const frame = iframeRef.current;
      if (!frame?.contentWindow) {
        return;
      }

      collectFlowRef.current = flow;
      clearCollectTimeout();
      collectTimeoutRef.current = beginAwaitingCollectRequest({
        awaiting: data,
        decision,
        postMessage: (messageValue, targetOrigin) => {
          frame.contentWindow?.postMessage(messageValue, targetOrigin);
        },
        scheduleTimeout: (callback, delay) =>
          setTimeout(() => {
            collectFlowRef.current = null;
            callback();
          }, delay),
        onCollectingChange: setCollectingDecision,
        onStatusChange: setSubmitStatus,
        onErrorChange: setSubmitError,
      });
    },
    [clearCollectTimeout, data],
  );

  const submitAggregatedPayload = useCallback(
    async (
      forms: FormActiveAwaiting["forms"],
      collectedParams: AIAwaitFormSubmitParamData[],
      autoSubmit = timeoutExpired,
    ) => {
      if (!onSubmit) {
        setSubmitStatus("");
        setSubmitError(t("awaiting.submit.missingHandler"));
        return;
      }

      setSubmitStatus(autoSubmit ? "autoSubmitting" : "submitting");
      setSubmitError("");
      const result = await onSubmit(
        buildAggregatedAwaitingSubmitPayload(
          {
            ...data,
            forms,
          },
          collectedParams,
        ),
      );
      const errorText = getSubmitErrorText(result);
      if (errorText) {
        setSubmitStatus("");
        setSubmitError(
          t("awaiting.submit.failedWithDetail", { detail: errorText }),
        );
        return;
      }
      setSubmitStatus("");
      setSubmitError("");
    },
    [data, onSubmit, t, timeoutExpired],
  );

  const handleAutoSubmit = useCallback(() => {
    if (
      resolved ||
      submitStatus === "submitting" ||
      submitStatus === "autoSubmitting" ||
      Boolean(collectingDecision)
    ) {
      return;
    }

    setTimeoutExpired(true);

    if (iframeRef.current?.contentWindow && data.viewportHtml) {
      requestCollectFromFrame("submit", { type: "submit" });
      return;
    }

    void submitAggregatedPayload(data.forms, [], true);
  }, [
    collectingDecision,
    data.forms,
    data.viewportHtml,
    requestCollectFromFrame,
    resolved,
    submitAggregatedPayload,
    submitStatus,
  ]);

  const timeoutCountdown = useAwaitingTimeoutCountdown({
    awaitingKey: data.key,
    timeout: data.timeout,
    createdAt: data.createdAt,
    onExpire: handleAutoSubmit,
  });

  useEffect(() => {
    activeKeyRef.current = data.key;
  }, [data.key]);

  useEffect(() => {
    clearCollectTimeout();
    collectFlowRef.current = null;
    requestedKeyRef.current = "";
    currentFrameKeyRef.current = "";
    lastPostedSignatureRef.current = "";
    setActiveFormIndex(0);
    setCollectingDecision(null);
    setSubmitStatus("");
    setSubmitError("");
    setTimeoutExpired(false);
    setRejectReason("");
    setFooterDecision(undefined);
  }, [clearCollectTimeout, data.key]);

  useEffect(() => {
    setActiveFormIndex((prev) =>
      clampAwaitingFormIndex(prev, data.forms.length),
    );
  }, [data.forms.length]);

  useEffect(() => {
    const noticeKey =
      data.resolutionReason === "timeout"
        ? "awaiting.timeoutResolved"
        : data.resolutionReason === "remote_answered"
          ? "awaiting.remoteAnswered"
          : "";
    if (!noticeKey) {
      resolutionHandledRef.current = false;
      return;
    }
    if (resolutionHandledRef.current) {
      return;
    }
    clearCollectTimeout();
    collectFlowRef.current = null;
    resolutionHandledRef.current = true;
    setCollectingDecision(null);
    setSubmitStatus("");
    setSubmitError("");
    void message.info(t(noticeKey));
    onResolved?.();
  }, [
    clearCollectTimeout,
    data.resolutionReason,
    onResolved,
    t,
  ]);

  useEffect(
    () => () => {
      clearCollectTimeout();
      collectFlowRef.current = null;
    },
    [clearCollectTimeout],
  );

  useEffect(() => {
    if (!data.viewportKey || data.viewportHtml || data.loading) {
      return;
    }
    if (requestedKeyRef.current === data.key) {
      return;
    }

    const expectedKey = data.key;
    requestedKeyRef.current = expectedKey;
    onPatch?.({
      loading: true,
      loadError: "",
      viewportHtml: "",
    });

    getViewport(data.viewportKey)
      .then((response) => {
        if (activeKeyRef.current !== expectedKey) {
          return;
        }
        const payload = response.data as Record<string, unknown> | null;
        const html =
          typeof payload?.html === "string" ? payload.html.trim() : "";
        if (!html) {
          throw new Error("Viewport response does not contain html");
        }
        onPatch?.({
          loading: false,
          loadError: "",
          viewportHtml: html,
        });
      })
      .catch((error) => {
        if (activeKeyRef.current !== expectedKey) {
          return;
        }
        onPatch?.({
          loading: false,
          loadError: t("awaiting.load.failedWithDetail", {
            detail: (error as Error).message,
          }),
          viewportHtml: "",
        });
      });
  }, [data.key, data.loading, data.viewportHtml, data.viewportKey, onPatch, t]);

  useEffect(() => {
    if (!data.viewportHtml || !iframeRef.current) {
      return;
    }

    const frame = iframeRef.current;
    const expectedKey = data.key;
    const sendInit = () => {
      if (activeKeyRef.current !== expectedKey) {
        return;
      }
      currentFrameKeyRef.current = expectedKey;
      postToFrame("init");
    };

    return bindAwaitingInitListener(frame, sendInit);
  }, [data.key, data.viewportHtml, postToFrame]);

  useEffect(() => {
    if (!data.viewportHtml) {
      return;
    }
    if (currentFrameKeyRef.current !== data.key) {
      return;
    }
    if (!lastPostedSignatureRef.current) {
      return;
    }
    if (lastPostedSignatureRef.current === viewportSignature) {
      return;
    }

    postToFrame("update");
  }, [data.key, data.viewportHtml, postToFrame, viewportSignature]);

  useEffect(() => {
    const onWindowMessage = async (event: MessageEvent) => {
      if (event.source !== iframeRef.current?.contentWindow) {
        return;
      }

      if (isAwaitingFrameCloseMessage(event.data)) {
        clearCollectTimeout();
        collectFlowRef.current = null;
        setCollectingDecision(null);
        if (!onSubmit) {
          setSubmitStatus("");
          setSubmitError(t("awaiting.submit.missingHandler"));
          return;
        }
        setSubmitStatus("submitting");
        setSubmitError("");
        const result = await onSubmit(buildCancelAwaitingSubmitPayload(data));
        const errorText = getSubmitErrorText(result);
        if (errorText) {
          setSubmitStatus("");
          setSubmitError(
            t("awaiting.submit.failedWithDetail", { detail: errorText }),
          );
          return;
        }
        setSubmitStatus("");
        setSubmitError("");
        onClose?.();
        return;
      }

      const payload = readAwaitingSubmitPayload(event.data, data);
      if (!payload) {
        reportInvalidAwaitingSubmitPayload(
          data.awaitingId,
          event.data,
          setSubmitError,
        );
        return;
      }
      clearCollectTimeout();
      clearAwaitingCollectRequest(clearTimeout, null, {
        onCollectingChange: setCollectingDecision,
      });
      const flow = collectFlowRef.current;
      collectFlowRef.current = null;
      const collectedParams = payload.params as AIAwaitFormSubmitParamData[];
      const nextForms = mergeSubmittedParamsIntoAwaitingForms(
        data.forms,
        collectedParams,
      );

      if (nextForms !== data.forms) {
        onPatch?.({ forms: nextForms });
      }

      if (flow?.type === "switch") {
        setActiveFormIndex(
          clampAwaitingFormIndex(flow.nextIndex, nextForms.length),
        );
        setSubmitStatus("");
        setSubmitError("");
        return;
      }

      if (flow?.type === "reject") {
        setSubmitStatus("submitting");
        setSubmitError("");
        const result = await onSubmit?.(
          buildRejectAwaitingSubmitPayload(data, flow.reason, nextForms),
        );
        const errorText = getSubmitErrorText(result);
        if (errorText) {
          setSubmitStatus("");
          setSubmitError(
            t("awaiting.submit.failedWithDetail", { detail: errorText }),
          );
          return;
        }
        setSubmitStatus("");
        setSubmitError("");
        return;
      }

      await submitAggregatedPayload(nextForms, collectedParams);
    };

    window.addEventListener("message", onWindowMessage);
    return () => window.removeEventListener("message", onWindowMessage);
  }, [
    clearCollectTimeout,
    data,
    onClose,
    onPatch,
    onSubmit,
    submitAggregatedPayload,
    t,
  ]);

  const switchDisabled =
    data.loading ||
    !data.viewportHtml ||
    Boolean(collectingDecision) ||
    submitStatus === "submitting" ||
    submitStatus === "autoSubmitting";

  const submitStatusText = submitStatus
    ? t(`awaiting.status.${submitStatus}`)
    : "";

  const handleSwitchForm = useCallback(
    (nextIndex: number) => {
      const resolvedIndex = clampAwaitingFormIndex(
        nextIndex,
        data.forms.length,
      );
      if (resolvedIndex === activeFormIndex) {
        return;
      }
      if (!iframeRef.current?.contentWindow || !data.viewportHtml) {
        setActiveFormIndex(resolvedIndex);
        return;
      }
      requestCollectFromFrame("submit", {
        type: "switch",
        nextIndex: resolvedIndex,
      });
    },
    [
      activeFormIndex,
      data.forms.length,
      data.viewportHtml,
      requestCollectFromFrame,
    ],
  );

  const handleReject = useCallback(async () => {
    const trimmedReason = rejectReason.trim();

    if (!onSubmit) {
      setSubmitStatus("");
      setSubmitError(t("awaiting.submit.missingHandler"));
      return;
    }

    if (iframeRef.current?.contentWindow && data.viewportHtml) {
      requestCollectFromFrame("submit", {
        type: "reject",
        reason: trimmedReason,
      });
      return;
    }
    setSubmitStatus("submitting");
    setSubmitError("");
    const result = await onSubmit(
      buildRejectAwaitingSubmitPayload(data, trimmedReason),
    );
    const errorText = getSubmitErrorText(result);
    if (errorText) {
      setSubmitStatus("");
      setSubmitError(
        t("awaiting.submit.failedWithDetail", { detail: errorText }),
      );
      return;
    }
    setSubmitStatus("");
    setSubmitError("");
  }, [data, onSubmit, rejectReason, requestCollectFromFrame, t]);

  const reasonInputDisabled =
    data.loading ||
    !onSubmit ||
    Boolean(collectingDecision) ||
    submitStatus === "submitting" ||
    submitStatus === "autoSubmitting";

  const footerReadOnly =
    data.loading ||
    !onSubmit ||
    Boolean(collectingDecision) ||
    submitStatus === "submitting" ||
    submitStatus === "autoSubmitting";

  const handleFooterDecisionSubmit = useCallback(
    (decision: AwaitingFooterDecision) => {
      if (footerReadOnly || (decision === "submit" && !data.viewportHtml)) {
        return;
      }

      setFooterDecision(decision);
      if (decision === "submit") {
        requestCollectFromFrame("submit", { type: "submit" });
        return;
      }
      void handleReject();
    },
    [data.viewportHtml, footerReadOnly, handleReject, requestCollectFromFrame],
  );
  const hostRef = useRef<HTMLDivElement>(null);
  const radioItemsRef = useRef<RadioRef[]>([]);
  useKeyboard({
    getAllHost: () => hostRef.current?.querySelectorAll('[tabIndex="0"]'),
    onEnter: (element) => {
      const index = Number(element.dataset.index);
      if (!Number.isFinite(index)) {
        return;
      }
      radioItemsRef.current?.[index]?.input?.click();
    },
    // onKeyDown: (e) => {
    //   if (isEditableKeyboardTarget(e.target)) {
    //     return;
    //   }
    //   if (!/^[1-9]$/.test(e.key)) {
    //     return;
    //   }
    //   e.preventDefault();
    //   e.stopPropagation();
    //   planQuestionRef.current?.check(Number(e.key) - 1);
    // },
  });
  return (
    <div className={AWAITING_PANEL_CLASS_NAME} id="awaiting-html-panel">
      <div className={AWAITING_PANEL_HEADER_CLASS_NAME}>
        <div className={AWAITING_PANEL_HEADER_MAIN_CLASS_NAME}>
          <strong className={AWAITING_PANEL_TITLE_CLASS_NAME}>
            {panelCaption}
          </strong>
        </div>
        <div className={AWAITING_PANEL_HEADER_SIDE_CLASS_NAME}>
          {timeoutCountdown.label && (
            <span className={AWAITING_TIMEOUT_BADGE_CLASS_NAME}>
              {timeoutExpired &&
              (submitStatus === "collecting" ||
                submitStatus === "submitting" ||
                submitStatus === "autoSubmitting")
                ? t("awaiting.status.autoSubmitting")
                : t("awaiting.timeout.countdown", {
                    label: timeoutCountdown.label,
                  })}
            </span>
          )}
          {data.forms.length > 1 && (
            <div className={AWAITING_PANEL_FORM_SWITCHER_CLASS_NAME}>
              <Button
                disabled={switchDisabled || activeFormIndex <= 0}
                icon={<MaterialIcon name="chevron_left" className="tw:text-xs" />}
                size="small"
                type="text"
                onClick={() => handleSwitchForm(activeFormIndex - 1)}
              />
              <span
                className={AWAITING_PANEL_FORM_SWITCHER_LABEL_CLASS_NAME}
                title={
                  currentForm?.title ||
                  currentForm?.action ||
                  currentForm?.id ||
                  ""
                }
              >
                {activeFormIndex + 1} / {data.forms.length}
                {currentForm && (
                  <>
                    {" "}
                    ·{" "}
                    {currentForm.title || currentForm.action || currentForm.id}
                  </>
                )}
              </span>
              <Button
                disabled={
                  switchDisabled || activeFormIndex >= data.forms.length - 1
                }
                icon={<MaterialIcon name="chevron_right" className="tw:text-xs" />}
                size="small"
                type="text"
                onClick={() => handleSwitchForm(activeFormIndex + 1)}
              />
            </div>
          )}
        </div>
      </div>

      {data.loading && (
        <div className="status-line tw:mt-0.5">
          {t("awaiting.load.loading")}
        </div>
      )}
      {data.loadError && (
        <div className={AWAITING_PANEL_ERROR_CLASS_NAME}>{data.loadError}</div>
      )}
      {!data.loading && !data.loadError && !data.viewportHtml && (
        <div className={AWAITING_PANEL_EMPTY_CLASS_NAME}>
          {t("awaiting.load.waiting")}
        </div>
      )}
      {data.viewportHtml && (
        <iframe
          ref={iframeRef}
          className={FRONTEND_TOOL_FRAME_CLASS_NAME}
          id="awaiting-html-frame"
          srcDoc={data.viewportHtml}
          sandbox="allow-scripts allow-popups allow-same-origin"
          title={`awaiting-${data.viewportKey}`}
        />
      )}

      <div ref={hostRef} className={AWAITING_PANEL_FOOTER_CLASS_NAME}>
        <Radio.Group
          className={AWAITING_PANEL_RADIOGROUP_CLASS_NAME}
          value={footerDecision}
          disabled={footerReadOnly}
        >
          <Radio
            value="submit"
            ref={(radioRef) => {
              if (radioRef) {
                radioItemsRef.current[0] = radioRef;
              }
            }}
            className={AWAITING_PANEL_OPTION_CLASS_NAME}
            disabled={footerReadOnly || !data.viewportHtml}
            onClick={() => {
              handleFooterDecisionSubmit("submit");
            }}
          >
            <Flex
              gap={10}
              align="center"
              tabIndex={0}
              data-index={0}
              className="tw:outline-none"
            >
              <span className={AWAITING_PANEL_OPTION_INDEX_CLASS_NAME}>1</span>
              <span className={AWAITING_PANEL_OPTION_LABEL_CLASS_NAME}>
                {submitStatusText
                  ? submitStatusText
                  : t("awaiting.action.approve")}
              </span>
              <span className={AWAITING_PANEL_SUBMIT_TAIL_CLASS_NAME}>
                {t("awaiting.hint.submitEditable")}
              </span>
            </Flex>
          </Radio>
          <Radio
            value="reject"
            ref={(radioRef) => {
              if (radioRef) {
                radioItemsRef.current[1] = radioRef;
              }
            }}
            className={AWAITING_PANEL_FREE_TEXT_OPTION_CLASS_NAME}
            disabled={footerReadOnly || !data.viewportHtml}
            onClick={() => {
              handleFooterDecisionSubmit("reject");
            }}
          >
            <Flex gap={10} align="center">
              <span className={AWAITING_PANEL_OPTION_INDEX_CLASS_NAME}>2</span>
              <span className={AWAITING_PANEL_OPTION_LABEL_CLASS_NAME}>
                {t("awaiting.action.reject")}
              </span>
              <Input
                aria-label={t("awaiting.rejectReason.placeholder")}
                disabled={reasonInputDisabled}
                variant="borderless"
                tabIndex={0}
                placeholder={t("awaiting.rejectReason.placeholder")}
                value={rejectReason}
                onChange={(event) => {
                  setRejectReason(event.target.value);
                  setFooterDecision("reject");
                }}
                onClick={(event) => {
                  event.stopPropagation();
                }}
                onPressEnter={() => {
                  if (!rejectReason) {
                    return;
                  }
                  handleFooterDecisionSubmit("reject");
                }}
                className="tw:p-0 tw:text-xs"
              />
            </Flex>
          </Radio>
        </Radio.Group>
      </div>
      {submitError && (
        <div className="system-alert tw:mt-0.5">{submitError}</div>
      )}
    </div>
  );
};
